import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Lock, Mail, ArrowRight, Home, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { loginClient, clearMessage } from '../Redux/Actions/ClientActions';
import LOGKDO from '../image/LogoM.png';
import './ClientAuth.css';

const ClientLogin = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clientAuthState = useSelector(state => state.clientReducer) || {};
  const {
    loading = false,
    isAuthenticated = false,
    message = null,
    error = null
  } = clientAuthState;

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/my-points');
    }

    if (message || error) {
      const timer = setTimeout(() => {
        dispatch(clearMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, message, error, navigate, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!credentials.email.trim()) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(credentials.email)) newErrors.email = 'Email invalide';
    if (!credentials.password) newErrors.password = 'Le mot de passe est requis';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      const result = await dispatch(loginClient(credentials.email, credentials.password));
      if (result.success) {
        navigate('/my-points');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="auth-mobile-container">
      <div className="auth-content-wrapper">
        <header className="auth-brand-section">
          <div className="flex justify-center mb-6">
            <img src={LOGKDO} alt="Millix Logo" className="h-16 w-auto object-contain" />
          </div>
          <h1 className="auth-title">Bienvenue</h1>
          <p className="auth-subtitle">Connectez-vous pour voir vos cadeaux <br /> et cumuler vos points.</p>
        </header>

        <main className="auth-card">
          {message && (
            <div className={`auth-banner ${message.type === 'success' ? 'success' : 'error'}`}>
              <AlertCircle size={18} />
              <span>{message.text}</span>
            </div>
          )}

          {error && !message && (
            <div className="auth-banner error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="auth-form-group">
              <label className="auth-label">Email</label>
              <div className="auth-input-wrapper">
                <input
                  type="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  className="auth-input"
                  disabled={loading}
                />
                <Mail className="auth-input-icon" size={18} />
              </div>
              {errors.email && <p className="text-destructive text-xs ml-2 mt-1">{errors.email}</p>}
            </div>

            <div className="auth-form-group">
              <label className="auth-label">Mot de passe</label>
              <div className="auth-input-wrapper auth-input-password">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="auth-input"
                  disabled={loading}
                />
                <Lock className="auth-input-icon" size={18} />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-destructive text-xs ml-2 mt-1">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  Se connecter
                  <ArrowRight size={20} className="ml-1" />
                </>
              )}
            </button>
          </form>

          <footer className="auth-footer">
            <p className="auth-footer-text">
              Pas encore de compte ?
              <Link to="/register-client" className="auth-link">
                Créer un compte
              </Link>
            </p>
          </footer>
        </main>

        <div className="text-center">
          <Link to="/" className="auth-back-home">
            <Home size={16} />
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;